"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useState } from "react";
import { pickActiveTarget, type TargetPosition } from "./scrollSpy";

/** A page bottom is never hit to the pixel on fractional device scales. */
const BOTTOM_TOLERANCE = 2;
export interface UseScrollSpyOptions {
  /** Distance from the viewport top at which a target counts as read. */
  readingLine?: number;
}

/**
 * The id of the element the reader is looking at, out of `targetIds`. `null`
 * until the first of them scrolls into view, and for ids that are not in the
 * DOM.
 *
 * Starts as `null` on the server and on the first client render, so there is
 * nothing to hydrate; the first measurement happens in the effect, which also
 * covers a page entered mid-scroll (a restored position, an incoming `#hash`).
 */
export function useScrollSpy(targetIds, t0) {
  const $ = _c(10);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    readingLine: t2
  } = t1;
  const readingLine = t2 === undefined ? 0 : t2;
  const [activeId, setActiveId] = useState(null);
  let t3;
  if ($[2] !== targetIds) {
    t3 = targetIds.join("\n");
    $[2] = targetIds;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const targetIdsKey = t3;
  let t4;
  if ($[4] !== targetIdsKey) {
    t4 = targetIdsKey.split("\n").filter(Boolean);
    $[4] = targetIdsKey;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const targets = t4;
  let t5;
  let t6;
  if ($[6] !== readingLine || $[7] !== targets) {
    t5 = () => {
      let frame = 0;
      const measure = () => setActiveId(pickActiveTarget(readPositions(targets), {
        readingLine,
        viewportHeight: window.innerHeight,
        isAtBottom: isScrolledToBottom()
      }));
      const scheduleMeasure = () => {
        if (frame) {
          return;
        }
        frame = requestAnimationFrame(() => {
          frame = 0;
          measure();
        });
      };
      measure();
      window.addEventListener("scroll", scheduleMeasure, {
        passive: true
      });
      window.addEventListener("resize", scheduleMeasure);
      const resizeObserver = new ResizeObserver(scheduleMeasure);
      resizeObserver.observe(document.body);
      return () => {
        cancelAnimationFrame(frame);
        window.removeEventListener("scroll", scheduleMeasure);
        window.removeEventListener("resize", scheduleMeasure);
        resizeObserver.disconnect();
      };
    };
    t6 = [targets, readingLine];
    $[6] = readingLine;
    $[7] = targets;
    $[8] = t5;
    $[9] = t6;
  } else {
    t5 = $[8];
    t6 = $[9];
  }
  useEffect(t5, t6);
  return activeId;
}
function readPositions(ids: string[]): TargetPosition[] {
  return ids.flatMap(id => {
    const element = document.getElementById(id);
    return element ? [{
      id,
      top: element.getBoundingClientRect().top
    }] : [];
  });
}
function isScrolledToBottom(): boolean {
  return window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - BOTTOM_TOLERANCE;
}